import { ReactNode } from 'react';
import { ArrowRed } from '../../assets';
import { Disclaimer, Div, H5, Icon, lightTheme, P } from '../../styles';

type Props = {
  label: string;
  /** The value. A string is set in H5 like every other detail value; anything
   *  else is rendered as given, for a status dot or a line with a tooltip. */
  children: ReactNode;
  /** A quieter second line under the value. */
  hint?: ReactNode;
  /** Given, the red drill-in arrow sits at the row's right edge and pressing
   *  it calls this - the way Income Details opens its order. */
  onOpen?: () => void;
  /** Anything else that belongs at the right edge: a provider logo, a text
   *  button. */
  right?: ReactNode;
};

/**
 * One fact in a detail modal: a label over its value, with the row's right
 * edge kept free for the drill-in arrow or whatever else sits beside it.
 *
 * This is the shape Income Details, Transaction Details and the delivery
 * page all drew by hand, so the label is a plain P and the value an H5
 * exactly as they are there. Rows are spaced by the caller's own
 * `Separator $m="16px 0"`, again as those modals do - a row does not know
 * whether it is the last one.
 */
export const DetailRow = ({ label, children, hint, onOpen, right }: Props) => (
  <Div $flex $alignCenter $between $gap="16px">
    {/* min-width 0 so a long value can wrap or ellipse instead of pushing
        the arrow out of the row. */}
    <Div $minWidth="0">
      <P $m="0">{label}</P>
      {typeof children === 'string' ? <H5 $m="0">{children}</H5> : children}
      {hint && (
        <Disclaimer $m="4px 0 0 0" $color={lightTheme.lightGray}>
          {hint}
        </Disclaimer>
      )}
    </Div>

    {onOpen && (
      <Div $pointer $flexNone onClick={onOpen}>
        <Icon>
          <ArrowRed />
        </Icon>
      </Div>
    )}
    {right}
  </Div>
);
